import { useState, useRef, useEffect, useCallback } from 'react';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../Snackbar/types';
import { UPLOAD } from '../../config/constants';
import { FileUploadItem, UseUploadModalReturn, RecoverableSession } from './types';
import uploadPersistenceService from '../../services/uploadPersistenceService';
import { SessionManager } from '../../utils/SessionManager';
import { useUploadProgressActions } from '../../contexts/UploadProgressProvider/UploadProgressProvider';
import { UPLOAD_INTERNAL_ONLY, PROCESSING_DELETE_INTERNAL_ONLY } from '../../constants/accessMessages';
import { isInternalNetworkOnlyError, INTERNAL_NETWORK_ONLY_CODE } from '../../utils/internalNetworkError';

// ファイル名から拡張子を除去するヘルパー関数
const removeExtension = (filename: string): string => {
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex === -1 ? filename : filename.substring(0, dotIndex);
};

// ファイルが許可された形式かどうかをチェックするヘルパー関数
const isValidVideoFile = (file: File): boolean => {
  // 拡張子をチェック
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'mov') {
    return false;
  }

  // MIMEタイプをチェック
  if (file.type === 'video/quicktime' || file.type.includes('mov')) {
    return false;
  }

  // MP4ファイルであることを確認
  return file.type === 'video/mp4';
};

/**
 * アップロードモーダルのロジックを管理するカスタムフック
 */
export const useUploadModal = (
  onClose: () => void,
  onUploadStart?: (file: File) => void
): UseUploadModalReturn => {
  const showSnackbar = useSnackbar();
  const { startUpload } = useUploadProgressActions();

  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [uploading, setUploading] = useState<boolean>(false); // バックグラウンド移行中はtrue
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [recoverableSessions, setRecoverableSessions] = useState<RecoverableSession[]>([]);
  // 復元待機中のセッション情報（ファイル選択待ち）
  const [pendingRecovery, setPendingRecovery] = useState<RecoverableSession | null>(null);

  // ファイル入力の参照を作成
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef<boolean>(true);

  // 復元可能なセッションをチェック
  const checkRecoverableSessions = useCallback(async () => {
    try {
      const activeSession = SessionManager.getActiveSession();
      if (!activeSession?.token) return;

      const localSessions = await uploadPersistenceService.getAllSessions();
      const validSessions: RecoverableSession[] = [];

      for (const session of localSessions) {
        try {
          // バックエンドでセッション有効性を確認
          const response = await fetch(
            `/api/videos/upload/status/${session.sessionId}`,
            {
              headers: { Authorization: `Bearer ${activeSession.token}` },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.status === 'in_progress') {
              validSessions.push({
                sessionId: session.sessionId,
                fileName: session.fileName,
                fileSize: session.fileSize,
                progress: data.progress,
                uploadedChunks: data.uploadedChunks?.length || 0,
                totalChunks: data.totalChunks,
                metadata: session.metadata,
              });
            } else {
              // 完了済みまたはキャンセルされたセッションはIndexedDBから削除
              await uploadPersistenceService.deleteSession(session.sessionId);
            }
          } else if (response.status === 404) {
            // セッションが見つからない場合はIndexedDBから削除
            await uploadPersistenceService.deleteSession(session.sessionId);
          }
        } catch (error) {
          console.error(`セッション ${session.sessionId} の確認に失敗:`, error);
        }
      }

      if (isMountedRef.current) {
        setRecoverableSessions(validSessions);
      }
    } catch (error) {
      console.error('復元可能セッションのチェックに失敗:', error);
    }
  }, []);

  // セッションを復元
  const recoverSession = useCallback(async (sessionId: string): Promise<void> => {
    const session = recoverableSessions.find(s => s.sessionId === sessionId);
    if (!session) return;

    setPendingRecovery(session);

    showSnackbar(
      `「${session.fileName}」を再度選択してください。アップロードは ${session.progress}% から再開されます。`,
      SnackbarType.INFO
    );

    setRecoverableSessions(prev => prev.filter(s => s.sessionId !== sessionId));

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [recoverableSessions, showSnackbar]);

  // セッションを破棄
  const dismissSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      const activeSession = SessionManager.getActiveSession();
      if (activeSession?.token) {
        const response = await fetch(`/api/videos/upload/${sessionId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${activeSession.token}` },
        });

        // fetch は 4xx/5xx で reject しないため、明示的に検査する。
        // ここを見ないと、院外からの 403 INTERNAL_NETWORK_ONLY を素通りさせて
        // 「破棄しました」と表示しつつローカル記録だけ消す（＝サーバ側セッションが
        // in_progress のまま残り、復元リストからも消えて二度とキャンセルできない
        // 孤児セッションになる）。ローカルレコードはリトライ手段なので消さない。
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const message =
            response.status === 403 && errorData.code === INTERNAL_NETWORK_ONLY_CODE
              ? PROCESSING_DELETE_INTERNAL_ONLY
              : 'セッションの破棄に失敗しました';
          showSnackbar(message, SnackbarType.ERROR);
          return;
        }
      }
      await uploadPersistenceService.deleteSession(sessionId);
      setRecoverableSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      showSnackbar('セッションを破棄しました', SnackbarType.INFO);
    } catch (error) {
      console.error('セッション破棄エラー:', error);
      showSnackbar('セッションの破棄に失敗しました', SnackbarType.ERROR);
    }
  }, [showSnackbar]);

  useEffect(() => {
    isMountedRef.current = true;
    checkRecoverableSessions(); // initはAppレベルで行われているが念のため

    const showModalEvent = new Event('showUploadModal');
    window.dispatchEvent(showModalEvent);

    return () => {
      isMountedRef.current = false;
      const hideModalEvent = new Event('hideUploadModal');
      window.dispatchEvent(hideModalEvent);
    };
  }, [checkRecoverableSessions]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    const selectedFiles = Array.from(e.target.files || []);

    if (selectedFiles.length === 0) {
      setPendingRecovery(null);
      return;
    }

    const invalidFiles = selectedFiles.filter((file) => !isValidVideoFile(file));
    if (invalidFiles.length > 0) {
      showSnackbar('MP4形式のファイルのみアップロードできます。', SnackbarType.WARNING);
      setPendingRecovery(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const totalFiles = files.length + selectedFiles.length;
    if (totalFiles > UPLOAD.MAX_CONCURRENT_UPLOADS) {
      showSnackbar(`一度にアップロードできる動画は最大${UPLOAD.MAX_CONCURRENT_UPLOADS}つです。`, SnackbarType.WARNING);
      setPendingRecovery(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const newFiles = selectedFiles.map((file) => {
      if (pendingRecovery &&
        file.name === pendingRecovery.fileName &&
        file.size === pendingRecovery.fileSize) {
        return {
          file,
          progress: pendingRecovery.progress,
          status: 'pending' as const,
          title: pendingRecovery.metadata.title || removeExtension(file.name),
          description: pendingRecovery.metadata.description || '',
          visibility: (pendingRecovery.metadata.visibility || 'private') as 'private' | 'public' | 'hospital_only',
          canResume: true,
        };
      }
      return {
        file,
        progress: 0,
        status: 'pending' as const,
        title: removeExtension(file.name),
        description: '',
        visibility: 'private' as const,
      };
    });

    setPendingRecovery(null);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);

    // ... validation ...
    const invalidFiles = droppedFiles.filter((file) => !isValidVideoFile(file));
    if (invalidFiles.length > 0) {
      showSnackbar('MP4形式のファイルのみアップロードできます。', SnackbarType.WARNING);
      return;
    }
    const totalFiles = files.length + droppedFiles.length;
    if (totalFiles > UPLOAD.MAX_CONCURRENT_UPLOADS) {
      showSnackbar(`一度にアップロードできる動画は最大${UPLOAD.MAX_CONCURRENT_UPLOADS}つです。`, SnackbarType.WARNING);
      return;
    }

    const newFiles = droppedFiles.map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
      title: removeExtension(file.name),
      description: '',
      visibility: 'private' as const,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFileAtIndex = (index: number): void => {
    setFiles((prevFiles) => prevFiles.filter((_, idx) => idx !== index));
  };

  // ファイル削除
  const handleRemoveFile = (index: number): void => {
    removeFileAtIndex(index);
  };

  // 特定のファイルアップロードをキャンセルする
  // 特定のファイルをリストから削除する（キャンセル扱い）
  const handleCancelUpload = (index: number): void => {
    removeFileAtIndex(index);
  };

  // すべてキャンセル = リストクリア
  const cancelAllUploads = (): void => {
    setFiles([]);
  };



  const resetUploadSelection = (): void => {
    setFiles([]);
    setUploading(false);
    setIsDragging(false);
    setPendingRecovery(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // メインのアップロード処理
  const handleUpload = async (): Promise<void> => {
    if (files.length === 0) return;

    setUploading(true); // UIロック用

    try {
      // コンテキスト経由でバックグラウンドアップロードを開始
      const firstFile = files[0]?.file;
      const uploadConfigs = files.map(f => ({
        file: f.file,
        title: f.title,
        description: f.description,
        visibility: f.visibility,
        canResume: f.canResume,
      }));

      await startUpload(uploadConfigs);

      if (onUploadStart && firstFile) {
        onUploadStart(firstFile);
      }

      showSnackbar('アップロードをバックグラウンドで開始しました', SnackbarType.INFO);

      resetUploadSelection();

      // モーダルを閉じる
      onClose();
    } catch (error) {
      console.error('アップロード開始エラー', error);
      // 403 + INTERNAL_NETWORK_ONLY は汎用文言で潰さず専用文言を出す（UX / 仕様書 §2.9）
      const message = isInternalNetworkOnlyError(error)
        ? UPLOAD_INTERNAL_ONLY
        : 'アップロードの開始に失敗しました';
      showSnackbar(message, SnackbarType.ERROR);
      setUploading(false);
    }
  };

  const handleNormalClose = () => onClose();
  const onCloseWithReload = () => onClose();

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && !uploading) {
      onClose();
    }
  };

  // ファイル選択をトリガー
  const triggerFileInput = (): void => {
    if (uploading) {
      return;
    }

    if (fileInputRef.current) {
      try {
        fileInputRef.current.click();
      } catch (error) {
        console.error('click()実行中にエラーが発生しました:', error);
      }
    } else {
      console.error('fileInputRefが見つかりません');
    }
  };

  // ステータスに基づく色を取得
  const getStatusColor = (status: FileUploadItem['status']): string => {
    switch (status) {
      case 'completed':
        return '#10b981'; // 緑
      case 'error':
        return '#ef4444'; // 赤
      case 'uploading':
        return '#3b82f6'; // 青
      case 'paused':
        return '#f59e0b'; // 黄色
      case 'cancelled':
        return '#f97316'; // オレンジ
      default:
        return '#9ca3af'; // グレー
    }
  };

  return {
    files,
    uploading,
    isDragging,
    fileInputRef,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleCancelUpload,
    cancelAllUploads,
    handleUpload,
    handleNormalClose,
    onCloseWithReload,
    handleRemoveFile,
    handleOverlayClick,
    triggerFileInput,
    getStatusColor,
    recoverableSessions,
    recoverSession,
    dismissSession,
  };
};
